import React from "react";
import { cn } from "@/lib/utils";

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Inline error text shown below the input. Sets aria-invalid. */
  error?: string;
  /** Field label rendered above the input. Mono-uppercase. */
  label?: string;
  /** Optional hint shown right-aligned beside the label. */
  hint?: string;
  /** Optional helper text below the input when there's no error. */
  helper?: string;
  /** Marks the field as required — renders an asterisk after the label. */
  required?: boolean;
  /** Optional leading icon (rare; contact-style fields have none). */
  leadingIcon?: React.ReactNode;
  /** Optional trailing slot (e.g. password show/hide button). */
  trailingIcon?: React.ReactNode;
  /** Wrapper className override. */
  wrapperClassName?: string;
}

/**
 * System-design input field — matches the marketing site's contact-us
 * style, on a white surface.
 *
 * Layout:
 *   • Mono uppercase label · optional hint on the right
 *   • Pill-rounded (rounded-2xl) bordered input on white
 *   • Mono uppercase error message below
 *
 * Backwards-compatible with the previous API.
 */
export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      wrapperClassName,
      type,
      error,
      label,
      hint,
      helper,
      required,
      leadingIcon,
      trailingIcon,
      id,
      ...props
    },
    ref,
  ) => {
    const inputId = id || props.name;
    const describedBy = error
      ? `${inputId}-error`
      : helper
        ? `${inputId}-helper`
        : undefined;

    const inputCls = cn(
      // base
      "w-full px-4 py-3 rounded-2xl bg-white border outline-none transition-colors",
      "text-[15px] text-foreground placeholder:text-foreground-secondary/60",
      "focus:bg-white",
      "disabled:cursor-not-allowed disabled:opacity-60",
      // states
      error
        ? "border-red-500/70 focus:border-red-500"
        : "border-neutral-200 hover:border-neutral-300 focus:border-neutral-500",
      // dark mode
      "dark:bg-card dark:focus:bg-card dark:border-border dark:hover:border-border-hover dark:focus:border-foreground/40",
      // padding when icons present
      leadingIcon ? "ps-12" : undefined,
      trailingIcon ? "pe-12" : undefined,
      className,
    );

    return (
      <div className={cn(wrapperClassName)}>
        {label && (
          <div className="flex items-center justify-between mb-2">
            <label
              htmlFor={inputId}
              className="font-sans text-[10px] tracking-widest text-foreground-secondary uppercase"
            >
              {label}{" "}
              {required && <span className="text-foreground">*</span>}
            </label>
            {hint && (
              <span className="font-sans text-[10px] text-foreground-secondary/70">
                {hint}
              </span>
            )}
          </div>
        )}
        <div className="relative">
          {leadingIcon && (
            <span className="absolute start-4 inset-y-0 flex items-center text-foreground-secondary pointer-events-none">
              {leadingIcon}
            </span>
          )}
          <input
            id={inputId}
            type={type}
            ref={ref}
            aria-invalid={!!error}
            aria-describedby={describedBy}
            className={inputCls}
            {...props}
          />
          {trailingIcon && (
            <span className="absolute end-4 inset-y-0 flex items-center text-foreground-secondary">
              {trailingIcon}
            </span>
          )}
        </div>
        {error ? (
          <p
            id={`${inputId}-error`}
            className="mt-2 font-sans text-[10px] tracking-widest text-red-500 uppercase"
            role="alert"
          >
            {error}
          </p>
        ) : helper ? (
          <p
            id={`${inputId}-helper`}
            className="mt-2 font-sans text-[10px] tracking-widest text-foreground-secondary/70 uppercase"
          >
            {helper}
          </p>
        ) : null}
      </div>
    );
  },
);
Input.displayName = "Input";
